import { localized, msg, str } from '@lit/localize';
import type { PropertyValues } from 'lit';
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '@spectrum-web-components/icons-workflow/icons/sp-icon-open-in.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-shuffle.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-user.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chat.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-settings.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-people.js';
import '@spectrum-web-components/avatar/sp-avatar.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/popover/sp-popover.js';

import type { FeatureFlags, User } from '../api/types';
import { environment } from '../config/environment';
import { DEFAULT_FEATURE_FLAGS } from '../config/feature-flags';
import type { LocalePreference } from '../i18n/localization';
import { isInternalUser } from '../utils/access';

@customElement('module-switcher')
@localized()
export class ModuleSwitcher extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: calc(14px + env(safe-area-inset-top, 0px)) 6px calc(12px + env(safe-area-inset-bottom, 0px)) calc(6px + env(safe-area-inset-left, 0px));
      background: var(--color-module-switcher-bg);
      border-right: var(--border);
      width: var(--kv-module-switcher-width);
      box-sizing: border-box;
      z-index: 1;
    }
    :host(.menu-open) {
      z-index: 100;
    }
    @media (max-width: 768px) {
      :host {
        z-index: 60;
      }
    }
    .spacer {
      flex: 1;
    }
    .brand {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 9px;
      overflow: hidden;
    }
    .brand-logo {
      display: block;
      width: 100%;
      height: 100%;
    }
    .module-list {
      display: flex;
      flex-direction: column;
      gap: 5px;
      width: 100%;
      align-items: center;
    }
    .module-item {
      position: relative;
      width: 56px;
      min-height: 58px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 7px 0 6px;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      color: var(--color-module-switcher-color);
      font: inherit;
      cursor: pointer;
      transition:
        background 0.12s ease,
        color 0.12s ease,
        border-color 0.12s ease;
    }
    .module-item::before {
      position: absolute;
      left: -6px;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 999px;
      background: transparent;
      content: '';
    }
    .module-item:hover {
      background: var(--color-module-switcher-hover-bg);
      color: var(--color-app-text);
    }
    .module-item:focus-visible,
    .account-button:focus-visible {
      outline: 2px solid var(--color-sidebar-selected-border);
      outline-offset: 2px;
    }
    .module-item[aria-current='page'] {
      background: transparent;
      border-color: transparent;
      color: var(--color-module-switcher-selected);
    }
    .module-item[aria-current='page']::before {
      background: var(--color-module-switcher-selected-indicator);
    }
    .module-item sp-icon-open-in,
    .module-item sp-icon-shuffle,
    .module-item sp-icon-chat,
    .module-item sp-icon-user,
    .module-item sp-icon-settings,
    .module-item sp-icon-people {
      width: 19px;
      height: 19px;
    }
    .module-label {
      max-width: 54px;
      overflow: hidden;
      font-size: 9.5px;
      font-weight: 500;
      line-height: 1.1;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .avatar-wrap {
      position: relative;
    }
    .account-button {
      display: flex;
      width: 56px;
      min-height: 62px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 4px 2px;
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      color: var(--color-module-switcher-color);
      font: inherit;
      cursor: pointer;
    }
    .account-button:hover,
    .account-button.open {
      background: var(--color-module-switcher-hover-bg);
      color: var(--color-app-text);
    }
    sp-avatar {
      pointer-events: none;
    }
    .initials {
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: var(--color-module-switcher-initials-bg);
      color: var(--color-module-switcher-initials-text);
      font-size: 12px;
      font-weight: 600;
      box-shadow: inset 0 0 0 1px var(--color-app-border);
    }
    .account-label {
      max-width: 54px;
      overflow: hidden;
      font-size: 9.5px;
      font-weight: 500;
      line-height: 1.1;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .account-caret {
      color: var(--color-module-switcher-color);
      font-size: 10px;
      line-height: 1;
      transform: translateY(-2px);
    }
  `;

  @property({ attribute: false }) accessor user: User | undefined = undefined;
  @property({ type: String }) accessor selected: string = 'workflows';
  @property({ type: Boolean }) accessor menuOpen = false;
  @property({ type: String }) accessor theme: 'light' | 'dark' | 'system' =
    'system';
  @property({ type: String }) accessor localePreference: LocalePreference =
    'system';
  @property({ attribute: false })
  accessor featureFlags: FeatureFlags = { ...DEFAULT_FEATURE_FLAGS };
  @property({ type: Boolean }) accessor isAdmin = false;

  private select(id: string) {
    this.selected = id;
    this.dispatchEvent(
      new CustomEvent('switch-module', {
        detail: { id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  private selectFromMenu(id: string) {
    this.menuOpen = false;
    this.select(id);
  }

  private logout() {
    this.menuOpen = false;
    this.dispatchEvent(
      new CustomEvent('logout', { bubbles: true, composed: true })
    );
  }

  private onThemeChange(e: Event) {
    const target = e.currentTarget as {
      value?: 'light' | 'dark' | 'system';
    } | null;
    const value = target?.value;
    if (!value) return;
    this.menuOpen = false;
    this.dispatchEvent(
      new CustomEvent('theme-change', {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onLocaleChange(e: Event) {
    const target = e.currentTarget as {
      value?: LocalePreference;
    } | null;
    const value = target?.value;
    if (!value) return;
    this.menuOpen = false;
    this.dispatchEvent(
      new CustomEvent('locale-change', {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderModuleButton(id: string, label: string, icon: unknown) {
    const selected = this.selected === id;
    return html`
      <button
        type="button"
        class="module-item"
        aria-label=${label}
        aria-current=${selected ? 'page' : 'false'}
        title=${label}
        @click=${() => this.select(id)}
      >
        ${icon}
        <span class="module-label">${label}</span>
      </button>
    `;
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (changed.has('menuOpen')) {
      this.classList.toggle('menu-open', this.menuOpen);
    }
  }

  render() {
    const initials = (this.user?.name || this.user?.email || '?')
      .split(' ')
      .map((s) => s[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

    const avatarSrc = this.user?.id
      ? `${environment.apiBaseUrl}/avatar?id=${encodeURIComponent(
          this.user.id
        )}`
      : undefined;
    const fallbackUser = msg('User');
    const displayName = this.user?.name || this.user?.email || fallbackUser;
    const firstName = displayName.split(/\s+/)[0] || fallbackUser;

    const flags = this.featureFlags ?? DEFAULT_FEATURE_FLAGS;
    const canShow = (key: keyof FeatureFlags) => flags[key];
    const showAdmin = this.isAdmin || isInternalUser(this.user?.email);

    return html`
      <div class="brand" aria-label="Klarvos">
        <img class="brand-logo" src="/logo.svg" alt="" />
      </div>
      <nav class="module-list" aria-label=${msg('Modules')}>
        ${
          canShow('remoteapp')
            ? this.renderModuleButton(
                'remoteapp',
                msg('Remote'),
                html`<sp-icon-open-in></sp-icon-open-in>`
              )
            : null
        }
        ${
          canShow('workflows')
            ? this.renderModuleButton(
                'workflows',
                msg('Automate'),
                html`<sp-icon-shuffle></sp-icon-shuffle>`
              )
            : null
        }
        ${
          canShow('chat')
            ? this.renderModuleButton(
                'chat',
                msg('Chat'),
                html`<sp-icon-chat></sp-icon-chat>`
              )
            : null
        }
        ${
          canShow('agent')
            ? this.renderModuleButton(
                'agent',
                msg('Agent'),
                html`<sp-icon-user></sp-icon-user>`
              )
            : null
        }
        ${
          canShow('settings')
            ? this.renderModuleButton(
                'settings',
                msg('Settings'),
                html`<sp-icon-settings></sp-icon-settings>`
              )
            : null
        }
        ${
          showAdmin
            ? this.renderModuleButton(
                'admin',
                msg('Admin'),
                html`<sp-icon-people></sp-icon-people>`
              )
            : null
        }
      </nav>
      <div class="spacer"></div>
      <div class="avatar-wrap">
        <button
          type="button"
          class=${`account-button ${this.menuOpen ? 'open' : ''}`}
          aria-label=${msg(str`Account menu for ${displayName}`)}
          aria-expanded=${this.menuOpen ? 'true' : 'false'}
          title=${displayName}
          @click=${this.toggleMenu}
        >
          ${
            avatarSrc
              ? html`<sp-avatar
                label=${displayName}
                src=${avatarSrc}
                size="500"
              ></sp-avatar>`
              : html`<span class="initials">${initials}</span>`
          }
          <span class="account-label">${firstName}</span>
          <span class="account-caret" aria-hidden="true">⌄</span>
        </button>
        ${
          this.menuOpen
            ? html`
              <sp-popover
                open
                placement="right"
                style="position: fixed; z-index: 1000; bottom: 20px; left: 20px;"
              >
                <sp-menu selects="single">
                  <sp-menu-item>
                    ${msg('Theme')}
                    <sp-menu
                      slot="submenu"
                      selects="single"
                      .value=${this.theme}
                      @change=${this.onThemeChange}
                    >
                      <sp-menu-item
                        value="light"
                        ?selected=${this.theme === 'light'}
                        >${msg('Light')}</sp-menu-item
                      >
                      <sp-menu-item
                        value="dark"
                        ?selected=${this.theme === 'dark'}
                        >${msg('Dark')}</sp-menu-item
                      >
                      <sp-menu-item
                        value="system"
                        ?selected=${this.theme === 'system'}
                        >${msg('System')}</sp-menu-item
                      >
                    </sp-menu>
                  </sp-menu-item>
                  <sp-menu-item>
                    ${msg('Language')}
                    <sp-menu
                      slot="submenu"
                      selects="single"
                      .value=${this.localePreference}
                      @change=${this.onLocaleChange}
                    >
                      <sp-menu-item
                        value="system"
                        ?selected=${this.localePreference === 'system'}
                        >${msg('Browser')}</sp-menu-item
                      >
                      <sp-menu-item
                        value="en"
                        ?selected=${this.localePreference === 'en'}
                        >${msg('English')}</sp-menu-item
                      >
                      <sp-menu-item
                        value="de"
                        ?selected=${this.localePreference === 'de'}
                        >${msg('Deutsch')}</sp-menu-item
                      >
                    </sp-menu>
                  </sp-menu-item>
                  ${
                    canShow('usage')
                      ? html`<sp-menu-item @click=${() => this.selectFromMenu('usage')}
                          >${msg(html`Usage & Costs`)}</sp-menu-item
                        >`
                      : null
                  }
                  <sp-menu-item @click=${this.logout}>${msg('Logout')}</sp-menu-item>
                </sp-menu>
              </sp-popover>
            `
            : null
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'module-switcher': ModuleSwitcher;
  }
}
